import React, { FC, useState, useEffect, useRef, useCallback } from "react";
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from "formik";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import { loginUser, selectAuthLoading } from "store/slices/authSlice";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { socialLogin } from "store/slices/authSlice";
import { handle406LoginError } from "utils/authHandlers"; // Add this import
import {
  ILoginPayload,
  ISignInModel,
  IValues,
} from "Interfaces/interface.auth";
import LucideIcon from "utils/LucideIcon";
import { EMAIL_PATTERN } from "utils/validationRegex";
import { ClipLoader } from "react-spinners";
import { closeModal } from "store/slices/authModalSlice";
import { environment } from '../../environments/environment';


declare global {
  interface Window {
    google: any;
  }
}

const validateForm = (values: ILoginPayload) => {
  const errors: Partial<ILoginPayload> = {};
  if (!values.username) {
    errors.username = "Email is required";
  } else if (!EMAIL_PATTERN.test(values.username)) {
    errors.username = "Please enter a valid email address";
  }
  if (!values.password) {
    errors.password = "Password is required";
  }
  return errors;
};

interface ISignInModelWithVerify extends ISignInModel {
  onSwitchToVerifyEmail?: (email: string) => void;
}

const SignInForm: FC<ISignInModelWithVerify> = ({
  onSuccess,
  onSwitchToSignUp,
  onSwitchToVerifyEmail,
  setSubmittingForm,
}) => {
  const dispatch = useAppDispatch();
  const [passwordType, setPasswordType] = useState("password");
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const loading = useAppSelector(selectAuthLoading);

  const handleLogin = async (
    values: ILoginPayload,
    { setSubmitting }: FormikHelpers<ILoginPayload>
  ) => {
    try {
      setSubmittingForm(true);
      const result = await dispatch(loginUser(values)).unwrap();
      toast.success("Signed in successfully!");
    } catch (error: any) {
      const result = await handle406LoginError(error, values.username);

      if (result.shouldVerifyEmail && onSwitchToVerifyEmail) {
        onSwitchToVerifyEmail(result.userEmail!);
      }

      setSubmitting(false);
      setSubmittingForm(false);
    }
  };

  const handleGoogleCallback = useCallback(
    async (response: any) => {
      try {
        if (response.credential) {
          const result = await dispatch(
            socialLogin({ accesstoken: response.credential })
          ).unwrap();
          onSuccess();
        } else {
          throw new Error("No credential received");
        }
      } catch (err: any) {
        console.error("Google sign-in error:", err);
        toast.error(err.message || "Google sign-in failed. Please try again.");
      }
    },
    [dispatch, onSuccess]
  );

  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;

    const initializeGoogleSignIn = () => {
      if (window.google?.accounts?.id && googleButtonRef.current) {
        if (!environment.GOOGLE_CLIENT_ID) {
          console.error("Google Client ID is not defined in .env");
          return;
        }

        window.google.accounts.id.initialize({
          client_id: environment.GOOGLE_CLIENT_ID,
          callback: handleGoogleCallback,
          itp_support: true,
          auto_select: true,
          cancel_on_tap_outside: false,
        });

        try {
          window.google.accounts.id.renderButton(googleButtonRef.current, {
            theme: "outline",
            size: "large",
            type: "icon",
            text: "signin_with",
            width: "240px",
          });
        } catch (error) {
          console.error("Failed to render Google Sign-In button:", error);
        }

        window.google.accounts.id.prompt((notification: any) => {
          console.log("Google prompt notification:", notification);
        });

        if (intervalId) {
          clearInterval(intervalId);
          intervalId = null;
        }
      } else {
        console.warn("Google Identity Services or button ref not ready");
      }
    };

    const timer = setTimeout(() => {
      intervalId = setInterval(initializeGoogleSignIn, 500);
    }, 100);

    return () => {
      if (intervalId) clearInterval(intervalId);
      clearTimeout(timer);
    };
  }, [handleGoogleCallback]);

  return (
    <Formik
      initialValues={{ username: "", password: "" }}
      validate={validateForm}
      onSubmit={handleLogin}
      validateOnMount={true}
      validateOnBlur={true}
      validateOnChange={true}
    >
      {({ isSubmitting }) => {
        return (
          <Form className="space-y-3">
            <div>
              <label className="block text-neutral-500 font-semibold dark:text-neutral-200 text-xs">
                Email Address
              </label>
              <Field
                name="username"
                type="email"
                placeholder="name@company.com"
                disabled={isSubmitting}
                className="mt-1 block w-full rounded-lg border p-2 text-sm border-neutral-300 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <ErrorMessage
                name="username"
                component="div"
                className="text-red-500 text-xs"
              />
            </div>
            <div>
              <label className="block text-neutral-500 font-semibold dark:text-neutral-200 text-xs">
                Password
              </label>
              <div className="relative">
                <Field
                  name="password"
                  type={passwordType}
                  placeholder="Password"
                  disabled={isSubmitting}
                  className="mt-1 block w-full rounded-lg border p-2 text-sm border-neutral-300 disabled:cursor-not-allowed disabled:opacity-50"
                />
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-blue-500">
                  {passwordType === "password" ? (
                    <LucideIcon
                      name="EyeOff"
                      className="p-1 cursor-pointer"
                      onClick={() => setPasswordType("text")}
                    />
                  ) : (
                    <LucideIcon
                      name="Eye"
                      className="p-1 cursor-pointer"
                      onClick={() => setPasswordType("password")}
                    />
                  )}
                </div>
              </div>
              <ErrorMessage
                name="password"
                component="div"
                className="text-red-500 text-xs"
              />
            </div>
            <div className="flex flex-col gap-2 justify-between items-center">
              <ButtonPrimary
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting || loading}
                className="w-full"
              >
                {!isSubmitting && "Sign In"}
              </ButtonPrimary>
              <Link
                to={isSubmitting || loading ? "#" : "/forgot-password"}
                onClick={(e) => {
                  if (isSubmitting || loading) e.preventDefault();
                  dispatch(closeModal());
                }}
                className={`text-primary-500 hover:text-primary-600 text-xs font-body ml-3 
    ${isSubmitting || loading ? "pointer-events-none opacity-50" : ""}`}
              >
                Forgot password?
              </Link>
            </div>
            <div className="mt-3 text-center">
              <span className="text-xs text-neutral-500">Or sign in with</span>
              <div className="flex justify-center gap-3 mt-2">
                {loading && !isSubmitting ? (
                  <ClipLoader size={18} />
                ) : (
                  <div
                    id="google-button"
                    ref={googleButtonRef}
                    className={`google-signin-button min-w-[240px] min-h-[40px] flex items-center justify-center 
      ${isSubmitting || loading ? "pointer-events-none opacity-50" : ""}`}
                  >
                    <span className="text-neutral-500">
                      Loading Google Sign-In...
                    </span>
                  </div>
                )}
              </div>
            </div>
            <div className="text-center text-sm">
              Don't have an account?{" "}
              <button
                onClick={onSwitchToSignUp}
                type="button"
                disabled={isSubmitting || loading}
                className="text-primary-500 hover:text-primary-600 font-medium hover:underline transition-colors duration-300 disabled:pointer-events-none disabled:opacity-50"
              >
                Create one
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SignInForm;
